import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { PhoneCall, X, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useHelp } from "@/components/HelpWidget";
import { useSiteContent } from "@/hooks/useSiteContent";

// Compact "Connect" popup — shown ONLY on lawyer / legal-help pages (never
// site-wide). Keeps the rest of the site free of interruptive popups while
// still offering a quick callback where a visitor is actually looking for a
// lawyer. It is a fixed-position <aside> (out of document flow) so it never
// causes layout shift (CLS).
const MATCHERS = [
  /^\/supreme-court-lawyers(\/|$)/,       // SC lawyers directory + profiles
  /^\/lawyer-supreme-court(\/|$)/,        // Supreme Court lawyer landing
  /^\/lawyer-high-court(\/|$)/,           // High Court lawyer landing
  /^\/supreme-court-lawyer-for(\/|$)/,    // practice-area pages
  /^\/supreme-court-lawyer\/[^/]+/,       // city lawyer pages
  /^\/advice(\/|$)/,                      // free legal help
  /^\/free-legal-help(\/|$)/,
];

export const ConnectSidePopup = () => {
  const { pathname } = useLocation();
  const { open } = useHelp();
  const { content } = useSiteContent();
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  const enabled = MATCHERS.some((m) => m.test(pathname));

  // Re-show on route change; delay slightly so it never competes with LCP.
  useEffect(() => {
    setDismissed(false);
    setVisible(false);
    if (!enabled) return undefined;
    const t = setTimeout(() => setVisible(true), 1400);
    return () => clearTimeout(t);
  }, [pathname, enabled]);

  if (!enabled || dismissed) return null;
  const phone = content?.contact_phone || "";

  return (
    <aside
      aria-label="Connect with a Supreme Court advocate"
      data-testid="connect-side-popup"
      className={
        "fixed bottom-4 right-4 z-40 hidden w-[260px] max-w-[calc(100vw-2rem)] rounded-2xl border bg-card/95 p-4 shadow-xl backdrop-blur transition-all duration-200 sm:block " +
        (visible ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0 pointer-events-none")
      }
    >
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Close connect popup"
        data-testid="connect-side-popup-close"
        className="absolute right-1.5 top-1.5 flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X className="h-4 w-4" />
      </button>
      <p className="pr-8 text-xs font-semibold uppercase tracking-wide text-primary">Connect with us</p>
      <p className="mt-1 text-sm font-semibold leading-snug">Talk to a Supreme Court advocate</p>
      <p className="mt-1 text-xs text-muted-foreground">Free initial guidance. Request a callback and we respond within one working day.</p>
      <div className="mt-3 flex flex-col gap-2">
        <Button size="sm" onClick={open} data-testid="connect-side-popup-callback" className="h-10 justify-start gap-2">
          <PhoneCall className="h-4 w-4" /> Request a callback
        </Button>
        {phone ? (
          <Button size="sm" variant="outline" asChild data-testid="connect-side-popup-call" className="h-10">
            <a href={`tel:${phone.replace(/\s+/g, "")}`} className="justify-start gap-2"><MessageSquare className="h-4 w-4" /> Call {phone}</a>
          </Button>
        ) : null}
      </div>
    </aside>
  );
};

export default ConnectSidePopup;
